/**
 * MobileWatchList — league-scoped mobile Watch List (rebuilt).
 *
 * Data: global GET /api/watchlist (no league_id param) returns canonical players
 * with season + rolling_14_day stats and a MULTI-LEAGUE league_contexts[] array
 * (now including per-league baseline_price + current_market_value). The active
 * league is selected client-side via league_contexts.find(lc.league_id===leagueId)
 * — the desktop pattern. leagueId/league/selectedTeamId arrive as PROPS from
 * MobileTeamHome (under TeamChrome).
 *
 * Flush: 4-cell summary strip (Watched · Available · Owned · Season — divided cells,
 * no card) → Hitters/Pitchers underline tabs → 2-row WatchTable on MobileDynastyTable.
 * Tokens from the shared color service. TeamChrome + the team header banner are the
 * parent's (MobileTeamHome under TeamChrome active watch-list); this renders the
 * summary strip down.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { dynastyTheme } from '../../../services/colorService';
import { watchlistAPI, leaguesAPI } from '../../../services/apiService';
import { MobileDynastyTable } from '../components/MobileDynastyTable';
import { MobilePlayerModal, MobilePlayerCard } from '../components/player';
import MobileBulkPositionAssignmentModal from '../components/MobileBulkPositionAssignmentModal';
import { analyzeRosterCapacity } from '../../../utils/RosterCapacityUtils';
import { createMobileWatchHitterColumns, createMobileWatchPitcherColumns, getActiveContext } from '../components/watchListColumns';
import { isPitcherPosition } from '../../../utils/playerClassification';
// DD-MODAL-0723: tell the modal's ownership-bearing session cache the world changed.
import { invalidatePlayer } from '../components/player/hooks/playerDataCache';

const C = dynastyTheme.tokens.colors;
const GOLD = dynastyTheme.tokens.colors.primary;
const MUTED = C.muted; // cool-gray muted

function MobileWatchList({ leagueId = null, league = null, selectedTeamId = null }) {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('hitting');

  // Player modal/card
  const [selectedPlayerId, setSelectedPlayerId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCardOpen, setIsCardOpen] = useState(false);

  // Add-to-roster (FA) bulk flow — mirrors mobile FA Search.
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [playersToAdd, setPlayersToAdd] = useState([]);
  const [currentRoster, setCurrentRoster] = useState([]);
  const [capacityAnalysis, setCapacityAnalysis] = useState(null);

  const loadWatchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await watchlistAPI.getWatchList();
      if (res?.success !== false) {
        setPlayers(Array.isArray(res?.players) ? res.players : []);
      } else {
        setError(res?.message || 'Failed to load watch list');
      }
    } catch (err) {
      console.error('Watch list load failed:', err);
      setError(err?.message || 'Failed to load watch list');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadWatchList(); }, [loadWatchList]);

  // Split by position + sort by watch priority (desc), like desktop.
  const organized = useMemo(() => {
    const o = { hitting: [], pitching: [] };
    players.forEach((p) => {
      const isPitcher = isPitcherPosition(p?.info?.position);
      (isPitcher ? o.pitching : o.hitting).push(p);
    });
    const byPriority = (a, b) => (b?.watch_metadata?.priority || 0) - (a?.watch_metadata?.priority || 0);
    o.hitting.sort(byPriority);
    o.pitching.sort(byPriority);
    return o;
  }, [players]);

  // Summary counts via the active-league context status (same source as the Status
  // badge): 'available' = FA, 'owned'/'other_team' = Owned.
  const { availableCount, ownedCount } = useMemo(() => {
    let avail = 0; let owned = 0;
    players.forEach((p) => {
      const st = getActiveContext(p, leagueId)?.status;
      if (st === 'available') avail += 1;
      else if (st === 'owned' || st === 'other_team') owned += 1;
    });
    return { availableCount: avail, ownedCount: owned };
  }, [players, leagueId]);

  const seasonYear = league?.current_season || league?.season || new Date().getFullYear();

  const handlePlayerClick = useCallback((mlbId) => {
    if (!mlbId) return;
    setSelectedPlayerId(mlbId.toString());
    setIsModalOpen(true);
  }, []);

  const handleRemove = useCallback(async (playerId) => {
    const previous = players;
    setPlayers(players.filter((p) => p?.ids?.mlb !== playerId)); // optimistic
    try {
      await watchlistAPI.removeFromWatchList(playerId);
    } catch (err) {
      console.error('Remove from watchlist failed:', err);
      setPlayers(previous);
      setError('Failed to remove player');
    }
  }, [players]);

  // FA add-to-roster: load roster + capacity, open the bulk-position modal
  // (desktop/mobile-FA pattern). The active context supplies league_player_id.
  const handleAdd = useCallback(async (player) => {
    if (!leagueId || !league) {
      setError('Add to roster is only available inside a league.');
      return;
    }
    try {
      const rosterRes = await leaguesAPI.getMyRoster(leagueId);
      const roster = rosterRes?.roster || rosterRes?.data?.roster || [];
      setCurrentRoster(roster);
      setCapacityAnalysis(analyzeRosterCapacity(roster, league));
      // The bulk-position modal reads a SINGULAR league_context.financial.contract_salary
      // for the acquisition salary; the watchlist payload is league_contexts[]. Project
      // the active context down to that shape, charging the FA acquisition price
      // (baseline_price) as the salary — matching FA Search's add convention.
      const ctx = getActiveContext(player, leagueId);
      const prepared = {
        ...player,
        ids: { ...player.ids, league_player: ctx?.league_player_id },
        league_context: ctx
          ? { ...ctx, financial: { ...ctx.financial, contract_salary: ctx.financial?.baseline_price ?? null } }
          : null,
      };
      setPlayersToAdd([prepared]);
      setShowBulkModal(true);
    } catch (err) {
      console.error('Prepare add failed:', err);
      setError('Failed to load roster data');
    }
  }, [leagueId, league]);

  const handleBulkComplete = useCallback(async (assignmentData) => {
    try {
      for (const a of assignmentData) {
        await leaguesAPI.addPlayerToTeam(leagueId, {
          league_player_id: a.player?.ids?.league_player,
          salary: a.salary,
          contract_years: a.contract_years,
          roster_status: a.roster_status,
          roster_position: a.roster_position,
          start_contract: a.start_contract,
        });
        invalidatePlayer(leagueId, a.player?.ids?.mlb);
      }
      setShowBulkModal(false);
      setPlayersToAdd([]);
      loadWatchList();
    } catch (err) {
      console.error('Bulk add failed:', err);
      setError('Failed to add player to roster');
      throw err;
    }
  }, [leagueId, loadWatchList]);

  const columns = useMemo(() => {
    const cfg = { leagueId, onRemove: handleRemove, onAdd: handleAdd, onPlayerClick: handlePlayerClick };
    return activeTab === 'hitting'
      ? createMobileWatchHitterColumns(cfg)
      : createMobileWatchPitcherColumns(cfg);
  }, [activeTab, leagueId, handleRemove, handleAdd, handlePlayerClick]);

  const data = activeTab === 'hitting' ? organized.hitting : organized.pitching;

  return (
    <div>
      <style>{`.home-scroll::-webkit-scrollbar { display: none; }`}</style>

      {/* Summary strip — flush 4-cell divided row (Watched · Available · Owned · Season) */}
      <div style={{ display: 'flex', borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}` }}>
        {[
          { label: 'Watched', value: players.length, color: C.textHi },
          { label: 'Available', value: availableCount, color: C.success },
          { label: 'Owned', value: ownedCount, color: GOLD },
          { label: 'Season', value: seasonYear, color: C.textHi },
        ].map((cell, i) => (
          <div key={cell.label} style={{ flex: 1, textAlign: 'center', padding: '7px 2px', ...(i > 0 ? { borderLeft: `1px solid ${C.hairline}` } : null) }}>
            <div style={{ fontSize: 18, fontWeight: 900, lineHeight: 1.1, color: cell.color, fontVariantNumeric: 'tabular-nums' }}>{cell.value}</div>
            <div style={{ fontSize: 9, fontWeight: 600, letterSpacing: '.6px', textTransform: 'uppercase', color: C.neutral[500] }}>{cell.label}</div>
          </div>
        ))}
      </div>

      {/* Hitters / Pitchers underline tabs — title case, count in parens, inset gold underline */}
      <div style={{ display: 'flex', padding: '0 14px', borderBottom: `1px solid ${C.border}` }}>
        {['hitting', 'pitching'].map((t) => {
          const active = activeTab === t;
          const label = t === 'hitting' ? `Hitters (${organized.hitting.length})` : `Pitchers (${organized.pitching.length})`;
          return (
            <div
              key={t}
              onClick={() => setActiveTab(t)}
              style={{ flex: 1, textAlign: 'center', fontSize: 14, fontWeight: 700, padding: '9px 0', position: 'relative', cursor: 'pointer', color: active ? GOLD : C.neutral[400] }}
            >
              {label}
              {active && <span aria-hidden="true" style={{ position: 'absolute', left: '25%', right: '25%', bottom: 0, height: 2, background: GOLD, borderRadius: 2 }} />}
            </div>
          );
        })}
      </div>

      {loading ? (
        <div style={{ padding: '24px 8px', textAlign: 'center', color: MUTED, fontSize: 12 }}>Loading watch list…</div>
      ) : error ? (
        <div style={{ padding: '16px 8px', color: dynastyTheme.tokens.colors.error, fontSize: 12 }}>{error}</div>
      ) : data.length === 0 ? (
        <div style={{ padding: '24px 8px', textAlign: 'center', color: MUTED, fontSize: 12 }}>
          No {activeTab === 'hitting' ? 'hitters' : 'pitchers'} on your watch list.
        </div>
      ) : (
        <MobileDynastyTable
          data={data}
          columns={columns}
          tableType="player-list"
          rowsPerItem={2}
          rowTypes={['season', 'rolling']}
          stickyHeader={false}
          enableHorizontalScroll={true}
          enableVerticalScroll={false}
          maxHeight="none"
          minWidth="620px"
        />
      )}

      {/* Player modal / card */}
      <MobilePlayerModal
        playerId={selectedPlayerId}
        leagueId={leagueId}
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setTimeout(() => setSelectedPlayerId(null), 300); }}
        onExpandToCard={() => { setIsModalOpen(false); setIsCardOpen(true); }}
      />
      <MobilePlayerCard
        playerId={selectedPlayerId}
        leagueId={leagueId}
        isOpen={isCardOpen}
        onClose={() => { setIsCardOpen(false); setTimeout(() => setSelectedPlayerId(null), 300); }}
        onCollapse={() => { setIsCardOpen(false); setIsModalOpen(true); }}
      />

      {/* FA add-to-roster bulk-position modal */}
      {showBulkModal && leagueId && (
        <MobileBulkPositionAssignmentModal
          selectedPlayers={playersToAdd}
          league={league}
          capacityAnalysis={capacityAnalysis}
          currentRoster={currentRoster}
          onAssignAll={handleBulkComplete}
          onCancel={() => { setShowBulkModal(false); setPlayersToAdd([]); }}
          isVisible={showBulkModal}
        />
      )}
    </div>
  );
}

export default MobileWatchList;
